import React, { useState } from "react";
import { Trophy, Medal, Pencil } from "lucide-react";
import api from "@/lib/api";
import { getPlayer, savePlayer } from "@/lib/player";

const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

/** Weekly (Mon–Sun) Box Office Face-Off board: points = correct answers, shown for players who set a nickname. */
export const FaceOffLeaderboard = ({ board, onBoard, dateKey }) => {
  const [player, setPlayer] = useState(getPlayer);
  const [editing, setEditing] = useState(!player.name);
  const [name, setName] = useState(player.name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!board) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/movies/quiz/player", { player_id: player.id, name: name.trim(), date: dateKey });
      setPlayer(savePlayer({ ...player, name: data.name }));
      setName(data.name); setEditing(false); onBoard?.(data.leaderboard);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save that nickname"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="faceoff-leaderboard" className="mt-6 border border-ink/15 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> This week's board · {fmt(board.week_start)} – {fmt(board.week_end)}</p>
        <span data-testid="faceoff-board-players" className="text-[11px] text-ink-muted">{board.players} player{board.players === 1 ? "" : "s"} · points = correct answers</span>
      </div>
      {editing ? (
        <form onSubmit={submit} className="mt-3 flex flex-wrap items-center gap-2" data-testid="faceoff-nickname-form">
          <input data-testid="faceoff-nickname-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={20} placeholder="Pick a nickname for the board" className="flex-1 min-w-[200px] h-10 px-3 border border-ink/20 bg-paper text-sm text-ink focus:border-gold outline-none" />
          <button type="submit" disabled={busy || name.trim().length < 2} data-testid="faceoff-nickname-save" className="px-4 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">{player.name ? "Save" : "Join the board"}</button>
          {player.name && <button type="button" data-testid="faceoff-nickname-cancel" onClick={() => { setEditing(false); setName(player.name); }} className="text-sm text-ink-muted hover:text-ink">Cancel</button>}
          {err && <p data-testid="faceoff-nickname-error" className="w-full text-[12px] text-red-700">{err}</p>}
        </form>
      ) : (
        <p className="mt-2 text-[13px] text-ink">
          {board.me ? <>You're <strong data-testid="faceoff-me-rank">#{board.me.rank}</strong> with <strong>{board.me.points} pt{board.me.points === 1 ? "" : "s"}</strong> over {board.me.days} day{board.me.days === 1 ? "" : "s"} as <strong>{player.name}</strong>.</> : <>Playing as <strong>{player.name}</strong> — your next counted play joins the board.</>}
          <button type="button" data-testid="faceoff-nickname-edit" onClick={() => setEditing(true)} className="ml-2 inline-flex items-center gap-1 text-[12px] font-semibold text-gold hover:underline"><Pencil className="w-3 h-3" /> rename</button>
        </p>
      )}
      {board.top.length > 0 ? (
        <ol className="mt-3 divide-y divide-ink/10" data-testid="faceoff-board-list">
          {board.top.map((p) => (
            <li key={p.rank} data-testid={`faceoff-board-row-${p.rank}`} className={`flex items-center gap-3 py-1.5 text-[13px] ${board.me && board.me.rank === p.rank && board.me.name === p.name ? "bg-gold/10 -mx-2 px-2" : ""}`}>
              <span className={`w-6 text-center font-bold ${p.rank <= 3 ? "text-gold" : "text-ink-muted"}`}>{p.rank <= 3 ? <Medal className="w-4 h-4 inline" /> : p.rank}</span>
              <span className="flex-1 truncate font-semibold text-ink">{p.name}</span>
              <span className="text-ink-muted text-[12px]">{p.days}d · best {p.best}/5</span>
              <span className="w-12 text-right font-bold text-ink">{p.points} pt</span>
            </li>
          ))}
        </ol>
      ) : <p data-testid="faceoff-board-empty" className="mt-3 text-[13px] text-ink-muted">No named players yet this week — set a nickname and your score goes straight on.</p>}
    </div>
  );
};
